import { useState, useCallback, useRef, useMemo } from 'react';
import type { AnswerOption, GamePhase, GameState, GameEngineConfig, Question, QuestionResult } from '../types';
import { QUESTIONS_PER_ROUND } from '../constants';
import { createGameSessionWithOptions, getCurrentRoundQuestions, getGameSessionDetail, submitAnswer, type ApiQuestion } from '../api/gameApi';
import { mapApiQuestions } from '../utils/mapApiQuestion';

const WALK_DURATION = 1200;
const PRESENT_PAUSE = 0;

// The visitor sprite is hidden on narrow screens (hidden sm:block),
// so the walk-in animation is invisible there — skip the wait.
function getWalkDuration(): number {
  if (typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches) {
    return 0;
  }
  return WALK_DURATION;
}

const DEFAULT_STABILITY = 50;
const DEFAULT_DEATH_THRESHOLD = 0;

function buildInitialState(config: GameEngineConfig): GameState {
  return {
    phase: 'WALKING',
    stability: config.startingStability ?? DEFAULT_STABILITY,
    score: 0,
    backendSessionId: null,
    session: {
      currentQuestion: null,
      questionsAnswered: 0,
      questionResults: Array(QUESTIONS_PER_ROUND).fill('pending') as QuestionResult[],
      correctCount: 0,
      year: 1,
      isDead: false,
    },
    lastStabilityDelta: 0,
    lastChosenOptionId: null,
  };
}

interface UseGameEngineOptions {
  allowNewSessionCreation?: boolean;
}

export function useGameEngine(
  config: GameEngineConfig,
  resumeSessionId?: string | null,
  options?: UseGameEngineOptions,
) {
  const [state, setState] = useState<GameState>(() => buildInitialState(config));
  const [loadError, setLoadError] = useState<string | null>(null);
  const configRef = useRef(config);
  configRef.current = config;
  const allowNewSessionCreation = options?.allowNewSessionCreation ?? true;

  const isLong = config.version === 'long';
  const deathThreshold = config.deathThreshold ?? DEFAULT_DEATH_THRESHOLD;

  const roundQuestionsRef = useRef<Question[]>([]);
  const indexRef = useRef(0);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const sessionIdRef = useRef<string | null>(null);
  const resumeSessionIdRef = useRef<string | null>(resumeSessionId ?? null);
  resumeSessionIdRef.current = resumeSessionId ?? null;
  const hasAttemptedResumeRef = useRef(false);
  const startedAtRef = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const schedule = useCallback((fn: () => void, ms: number) => {
    timersRef.current.push(setTimeout(fn, ms));
  }, []);

  const setPhase = useCallback((phase: GamePhase) => {
    setState((s: GameState) => ({ ...s, phase }));
  }, []);

  const startDeciding = useCallback(() => {
    startedAtRef.current = Date.now();
    setPhase('DECIDING');
  }, [setPhase]);

  const loadNextQuestion = useCallback(() => {
    const idx = indexRef.current;
    if (idx >= roundQuestionsRef.current.length) {
      setState((s) => ({ ...s, phase: 'ROUND_COMPLETE' }));
      return;
    }

    const next = roundQuestionsRef.current[idx];
    indexRef.current = idx + 1;

    setState((s: GameState) => ({
      ...s,
      phase: 'WALKING',
      session: { ...s.session, currentQuestion: next },
      lastChosenOptionId: null,
      lastStabilityDelta: 0,
    }));

    const walkMs = getWalkDuration();
    schedule(() => setPhase('PRESENTING'), walkMs);
    schedule(startDeciding, walkMs + PRESENT_PAUSE);
  }, [startDeciding, schedule, setPhase]);

  const initRoundFromQuestions = useCallback((questions: Question[], preserveState: boolean) => {
    roundQuestionsRef.current = questions;
    indexRef.current = 0;

    const first = questions[0];
    indexRef.current = 1;

    if (preserveState) {
      setState((prev) => ({
        ...prev,
        phase: 'WALKING',
        session: {
          ...prev.session,
          currentQuestion: first,
          questionsAnswered: 0,
          questionResults: Array(questions.length).fill('pending') as QuestionResult[],
          correctCount: 0,
          year: prev.session.year + 1,
          isDead: false,
        },
        lastStabilityDelta: 0,
        lastChosenOptionId: null,
      }));
    } else {
      const fresh = buildInitialState(configRef.current);
      setState({
        ...fresh,
        backendSessionId: sessionIdRef.current,
        session: {
          ...fresh.session,
          currentQuestion: first,
          questionResults: Array(questions.length).fill('pending') as QuestionResult[],
        },
      });
    }

    const walkMs = getWalkDuration();
    schedule(() => setPhase('PRESENTING'), walkMs);
    schedule(startDeciding, walkMs + PRESENT_PAUSE);
  }, [startDeciding, schedule, setPhase]);

  const initRoundFromResume = useCallback((
    sessionId: string,
    detail: {
      score: number;
      earth_vitality: number;
      questions_correct: number;
      questions_answered: number;
      year: number;
    },
    apiQuestions: ApiQuestion[],
  ) => {
    const mappedQuestions = mapApiQuestions(apiQuestions);
    roundQuestionsRef.current = mappedQuestions;

    const firstUnansweredIndex = Math.max(
      0,
      apiQuestions.findIndex((q) => q.already_answered !== true),
    );
    const hasUnanswered = apiQuestions.some((q) => q.already_answered !== true);
    const currentQuestion = hasUnanswered ? mappedQuestions[firstUnansweredIndex] : null;
    indexRef.current = hasUnanswered ? firstUnansweredIndex + 1 : mappedQuestions.length;

    const questionResults = apiQuestions.map((q) => {
      if (!q.already_answered) return 'pending' as QuestionResult;
      return q.is_correct ? 'correct' as QuestionResult : 'wrong' as QuestionResult;
    });

    sessionIdRef.current = sessionId;
    setState((prev) => ({
      ...prev,
      phase: currentQuestion ? 'WALKING' : 'ROUND_COMPLETE',
      stability: detail.earth_vitality,
      score: detail.score,
      backendSessionId: sessionId,
      session: {
        ...prev.session,
        currentQuestion,
        questionsAnswered: detail.questions_answered,
        questionResults,
        correctCount: detail.questions_correct,
        year: detail.year,
        isDead: detail.earth_vitality <= deathThreshold,
      },
      lastStabilityDelta: 0,
      lastChosenOptionId: null,
    }));

    if (currentQuestion) {
      const walkMs = getWalkDuration();
      schedule(() => setPhase('PRESENTING'), walkMs);
      schedule(startDeciding, walkMs + PRESENT_PAUSE);
    }
  }, [deathThreshold, schedule, setPhase, startDeciding]);

  const startRound = useCallback((preserveState: boolean) => {
    clearTimers();
    const cfg = configRef.current;
    setLoadError(null);

    const canResumeExistingSession = !preserveState && !hasAttemptedResumeRef.current && !!resumeSessionIdRef.current;
    if (canResumeExistingSession) {
      hasAttemptedResumeRef.current = true;
      const existingSessionId = resumeSessionIdRef.current as string;
      Promise.all([
        getGameSessionDetail(existingSessionId),
        getCurrentRoundQuestions(existingSessionId),
      ])
        .then(([detail, round]) => {
          if (detail.status === 'completed') {
            throw new Error('Session is already completed.');
          }
          initRoundFromResume(existingSessionId, detail, round.questions);
        })
        .catch((resumeError) => {
          if (!allowNewSessionCreation) {
            console.error('[Game] Failed to resume the current custom game session.', resumeError);
            setLoadError('Could not load this custom game. Please return to the menu and join again.');
            hasAttemptedResumeRef.current = false;
            return;
          }

          console.info('[Game] Session resume failed, creating a new session instead.', resumeError);
          createGameSessionWithOptions(QUESTIONS_PER_ROUND, cfg.version, true)
            .then((data) => {
              sessionIdRef.current = data.session_id;
              setState((prev) => ({ ...prev, backendSessionId: data.session_id }));
              initRoundFromQuestions(mapApiQuestions(data.questions), preserveState);
            })
            .catch((err) => {
              console.error('Failed to load game session from backend:', err);
              setLoadError('Could not load game session from the server. Please retry.');
            });
        });
      return;
    }

    if (!allowNewSessionCreation) {
      setLoadError('Could not load this custom game. Please return to the menu and join again.');
      return;
    }

    createGameSessionWithOptions(QUESTIONS_PER_ROUND, cfg.version, false)
      .then((data) => {
        sessionIdRef.current = data.session_id;
        setState((prev) => ({ ...prev, backendSessionId: data.session_id }));
        initRoundFromQuestions(mapApiQuestions(data.questions), preserveState);
      })
      .catch((err) => {
        console.error('Failed to load game session from backend:', err);
        setLoadError('Could not load game session from the server. Please retry.');
      });
  }, [allowNewSessionCreation, clearTimers, initRoundFromQuestions, initRoundFromResume]);

  const beginGame = useCallback(() => startRound(false), [startRound]);

  const startNextRound = useCallback(() => startRound(true), [startRound]);

  const decide = useCallback(
    (optionId: string) => {
      setState((prev) => {
        if (prev.phase !== 'DECIDING' || !prev.session.currentQuestion) return prev;

        const chosen = prev.session.currentQuestion.options.find((o: AnswerOption) => o.id === optionId);
        if (!chosen) return prev;
        const isCorrect = chosen.isCorrect === true;
        const vitalityDelta = isLong ? 0 : chosen.stabilityImpact;
        const newStability = Math.max(0, Math.min(100, prev.stability + vitalityDelta));
        const result = isCorrect ? 'correct' : 'wrong';
        const results = [...prev.session.questionResults];
        results[prev.session.questionsAnswered] = result;

        const newCorrect = prev.session.correctCount + (isCorrect ? 1 : 0);

        const died = newStability <= deathThreshold;
        const elapsedMs = startedAtRef.current !== null
          ? Math.max(0, Date.now() - startedAtRef.current)
          : 0;

        startedAtRef.current = null;

        if (sessionIdRef.current) {
          const questionBankId = Number(prev.session.currentQuestion.questionId);
          submitAnswer(sessionIdRef.current, questionBankId, chosen.optionIndex, elapsedMs)
            .then((res) => {
              console.info('[Game] Backend answer result', {
                questionId: questionBankId,
                selectedOptionIndex: chosen.optionIndex,
                localIsCorrect: isCorrect,
                backendIsCorrect: res.is_correct,
                score: res.session.score,
                elapsedMs,
              });
              if (isCorrect !== res.is_correct) {
                console.warn('[Game] Local and backend correctness differ', {
                  questionId: questionBankId,
                  selectedOptionIndex: chosen.optionIndex,
                  localIsCorrect: isCorrect,
                  backendIsCorrect: res.is_correct,
                });
              }

              setState((old) => ({
                ...old,
                score: res.session.score,
                stability: res.session.earth_vitality,
                lastStabilityDelta: res.session.vitality_delta ?? old.lastStabilityDelta,
                phase: res.session.earth_vitality <= deathThreshold ? 'GAME_OVER' : old.phase,
                session: {
                  ...old.session,
                  isDead: res.session.earth_vitality <= deathThreshold,
                },
              }));
            })
            .catch((err) => {
              console.error('[Game] Failed to submit answer', {
                questionId: questionBankId,
                selectedOptionIndex: chosen.optionIndex,
                elapsedMs,
                error: err,
              });
            });
        }

        return {
          ...prev,
          phase: died ? 'GAME_OVER' as GamePhase : 'RESOLVING' as GamePhase,
          stability: newStability,
          score: isLong ? prev.score : Math.max(0, prev.score + vitalityDelta),
          lastStabilityDelta: vitalityDelta,
          lastChosenOptionId: optionId,
          session: {
            ...prev.session,
            questionsAnswered: prev.session.questionsAnswered + 1,
            questionResults: results,
            correctCount: newCorrect,
            isDead: died,
          },
        };
      });
    },
    [deathThreshold, isLong],
  );

  const nextQuestion = useCallback(() => {
    if (state.phase !== 'RESOLVING') return;

    loadNextQuestion();
  }, [state.phase, loadNextQuestion]);

  const restart = useCallback(() => {
    clearTimers();
    beginGame();
  }, [clearTimers, beginGame]);

  const actions = useMemo(
    () => ({ beginGame, decide, restart, startNextRound, nextQuestion }),
    [beginGame, decide, restart, startNextRound, nextQuestion],
  );

  return { state, actions, loadError };
}
